import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

type SwitchSize = 'sm' | 'md';

const TRACK_SIZE_CLASS: Record<SwitchSize, string> = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
};

const THUMB_SIZE_CLASS: Record<SwitchSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
};

const THUMB_TRANSLATE: Record<SwitchSize, { on: string; off: string }> = {
  sm: { on: 'translate-x-4', off: 'translate-x-1' },
  md: { on: 'translate-x-6', off: 'translate-x-1' },
};

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type' | 'value' | 'role' | 'onClick'> {
  /** Render-prop convention: boolean under `value`/`onChange(next)`, so
   * `<Switch {...field} />` spreads cleanly off FormField's render props. */
  value?: boolean;
  onChange: (next: boolean) => void;
  invalid?: boolean;
  size?: SwitchSize;
  /** Optional visible label rendered after the track. If omitted, the caller
   * must supply `aria-label` or `aria-labelledby` directly. */
  label?: string;
}

// `<button type="button" role="switch" aria-checked>` — NOT a checkbox
// styled to look like a toggle. `type="button"` is MANDATORY: a <button>
// inside a <form> defaults to type="submit", so an untyped switch would
// submit the form on click (and Space/Enter, which activate a <button> —
// this is also why it's a real <button> and not a `<div role="switch">`,
// which would need hand-rolled Space/Enter key handling to match).
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { value, onChange, onBlur, invalid, disabled, size = 'md', label, className = '', ...rest },
  ref,
) {
  const checked = Boolean(value);
  const track = THUMB_TRANSLATE[size];
  const button = (
    <button
      ref={ref}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      onBlur={onBlur}
      className={`focus-ring inline-flex ${TRACK_SIZE_CLASS[size]} shrink-0 items-center rounded-full border border-border transition-colors disabled:pointer-events-none disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-muted'
      } ${invalid ? 'ring-2 ring-destructive' : ''} ${className}`}
      {...rest}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
    >
      <span
        aria-hidden="true"
        className={`inline-block ${THUMB_SIZE_CLASS[size]} transform rounded-full bg-white shadow transition-transform ${
          checked ? track.on : track.off
        }`}
      />
    </button>
  );

  if (!label) return button;

  // Story 2-148 part 7 finding, fixed here: this was a plain <span> wrapper
  // — visually beside the switch, but with ZERO programmatic association,
  // so the built-in `label` prop this primitive's own type docstring
  // ("the caller must supply aria-label... if omitted") implies is a real
  // alternative to aria-label was actually producing an UNLABELED switch
  // (axe's `button-name`, `critical`) every place it was used this way. A
  // real `<label>` wrapper gives the switch an accessible name via native
  // HTML association — the same mechanism every hand-wrapped `<label><Switch
  // .../>text</label>` call site in this codebase already uses correctly
  // (ReportDesigner.tsx, DesignSystemPage.tsx); this makes the primitive's
  // OWN built-in `label` prop do the same thing instead of silently failing.
  return (
    <label className="inline-flex items-center gap-2">
      {button}
      <span className="text-sm text-foreground">{label}</span>
    </label>
  );
});
